// @flow
import * as React from 'react';

import { selectCartIsWorking } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';
import {
  Base,
  Block,
  BlockButtons,
  BlockHeading,
  Button,
  Text,
} from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';
import { useFieldWithTypeValue } from 'src/hooks/useFieldWithTypeValue';
import { selectSellableMailboxOffers } from 'src/modules/mailbox/offers';
import Price from 'src/shared/Price';

import {
  MAILBOX_COUNT_INPUT_NAME,
  MAILBOX_DURATION_INPUT_NAME,
  MAILBOX_TYPE_INPUT_NAME,
} from '../consts';
import {
  useMailboxLabel,
  useDurationTranslations,
  useMailboxPrice,
} from '../hooks';

import type { FormValues } from '../types/form';

import { MailboxCounter } from './MailboxCounter';

export function MailboxSummary({
  children,
}: {
  children: React.ChildrenArray<ExactReactElement_DEPRECATED<typeof Button>>,
}): null | React.MixedElement {
  const { __ } = useTranslator();

  const mailboxType = useFieldWithTypeValue<
    FormValues[typeof MAILBOX_TYPE_INPUT_NAME],
  >(MAILBOX_TYPE_INPUT_NAME);
  const durationValue = useFieldWithTypeValue<
    FormValues[typeof MAILBOX_DURATION_INPUT_NAME],
  >(MAILBOX_DURATION_INPUT_NAME);
  const countValue = useFieldWithTypeValue<
    FormValues[typeof MAILBOX_COUNT_INPUT_NAME],
  >(MAILBOX_COUNT_INPUT_NAME);

  const duration = parseInt(durationValue || 1, 10);
  const count = parseInt(countValue || 0, 10);

  const { currency, price, sharingId } = useMailboxPrice({
    mailboxType: mailboxType || 'standard_new',
    minDuration: duration,
  });

  const sellable = useAppSelector(state =>
    selectSellableMailboxOffers(state, sharingId),
  );

  const isCartIsWorking = useAppSelector(selectCartIsWorking);

  const getMailboxLabel = useMailboxLabel();
  const { toPriceLabel, toLabel } = useDurationTranslations();

  const { priceAfterTaxes: unitaryPrice = 0, durationUnit = 'm' } = price || {};

  const totalPrice = unitaryPrice * count * duration;

  const selectedConfiguration = sellable.find(
    box => box.mailbox_type === mailboxType,
  );
  const disableSubmit =
    !selectedConfiguration ||
    !countValue ||
    (selectedConfiguration?.duration && !durationValue);

  return selectedConfiguration ? (
    <Block>
      <Base flex flexCol gap="small">
        <BlockHeading fontSize="s">
          {__('%(type)s mailbox', {
            type: getMailboxLabel(
              selectedConfiguration.name,
              selectedConfiguration.capacity,
            ),
          })}{' '}
          {durationValue &&
            __('with a %(durationLabel)s renew', {
              durationLabel: toLabel[durationUnit][durationValue],
            })}
        </BlockHeading>
        <Base
          flex
          flexJustifyBetween
          flexAlignItemsBaseline
          flexWrap
          gap="small"
        >
          <Price
            currency={currency}
            price={unitaryPrice * duration}
            inline
            // $FlowFixMe[invalid-computed-prop]
            priceInfo={` ${toPriceLabel[`${duration}`]}`}
          />
          <MailboxCounter />
        </Base>
        <Base flex flexJustifyBetween flexWrap gap="small">
          <Text weight="semibold" size="big">
            {__('Total:')}
          </Text>
          <Price
            currency={currency}
            price={totalPrice}
            // $FlowFixMe[invalid-computed-prop]
            priceInfo={` ${toPriceLabel[`${duration}`]}`}
          />
        </Base>
      </Base>
      <BlockButtons>
        {children}
        <Button
          type="submit"
          loading={isCartIsWorking}
          disabled={disableSubmit}
        >
          {__('Add to cart')}
        </Button>
      </BlockButtons>
    </Block>
  ) : null;
}
